<template>
	<slot v-if="FLAG_DISABLE_TOOLTIPS || disabled" name="element" />
	<VTooltip
		v-else
		:classes="['tooltip']"
		:placement="placement"
		:triggers="triggersList"
		:distance="12"
		:autoHide="true"
	>
		<slot name="element" />
		<template #popper>
			<div
				class="tooltip"
				:style="{
					width: customMaxWidth
						? 'unset'
						: customWidth
							? customWidth
							: 'auto',
					maxWidth: customMaxWidth ? customMaxWidth : 'none',
				}"
			>
				<div class="header-container" v-if="heading">
					<div class="header">
						<span class="heading">{{ heading }}</span>
					</div>
					<DividerLine />
				</div>
				<div class="body">
					<div class="content">
						<slot name="tooltip" />
					</div>
				</div>
			</div>
		</template>
	</VTooltip>
</template>

<script lang="ts" setup>
import DividerLine from "@/components/SVG/DividerLine.vue";
import { FLAG_DISABLE_TOOLTIPS } from "@/config/constants";
import { Placements } from "@/types/ui";

interface Props {
	heading?: string | null;
	disabled?: boolean;
	customWidth?: string;
	customMaxWidth?: string;
	placement?: Placements;
}

const {
	heading = "",
	disabled = false,
	customWidth = null,
	placement = Placements.BOTTOM,
} = defineProps<Props>();

const triggersList = ["click", "touch"] as const; // "hover", "focus", "touch"
</script>

<style lang="scss">
.v-popper {
	&__inner {
		border-radius: 0.15rem !important;
		padding: 0 !important;
	}

	&__popper {
		.tooltip {
			@include flex-col-center;
			box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.1);
			border-radius: 0.15rem;
			background: url($path-images + "backgrounds/textures/window-texture-bg.avif")
				repeat center;
			background-size: 90% auto;
			padding: 0.125rem;
			width: auto;
			min-width: 7rem;
			overflow: hidden;

			.header {
				@include flex-row-center;
				padding: 0.25rem;
				width: 100%;

				&-container {
					@include flex-col-center;
				}

				.heading {
					position: relative;
					z-index: 1;
					padding-bottom: 0.25rem;
					color: #f4d278;
					font-weight: 800;
					font-size: 0.875rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
				}
			}

			.body {
				background: #f2dcb5;
				width: 100%;

				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 1 !important;
					// NOTE: Multiply blending is better for light colors
					mix-blend-mode: multiply !important;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 60%;
					width: 100%;
					height: 100%;
					content: "";
				}

				.content {
					@include flex-col-center;
					position: relative;
					z-index: 1;
					padding: 0.5rem;
					min-height: 3.375rem;

					& > * {
						color: #372615;
						font-size: 0.75rem;

						.tile-aggregation-details-wrapper {
							.price__value .shrink-text {
								color: #372615 !important;
								text-shadow: none;
							}
						}
					}
				}
			}
		}

		.v-popper__arrow-container {
			// base style for arrow
			::after {
				position: absolute;
				left: 50%;
				width: 0.875rem;
				height: 0.563rem;
				content: "";
				@include background(
					url($path-icons + "menu/tooltip-arrow.avif"),
					contain,
					center,
					no-repeat
				);

				z-index: 2;
			}

			.v-popper__arrow-outer {
				border-color: transparent !important;
				background: transparent !important;
			}
		}

		&[data-popper-placement^="bottom"] {
			.v-popper__arrow-container {
				::after {
					top: -0.188rem;
					transform: translateX(-50%);
				}
			}
		}

		&[data-popper-placement^="left"] {
			.v-popper__arrow-container {
				::after {
					left: -0.125rem;
					transform: translateX(-50%) rotate(90deg);
				}
			}
		}

		&[data-popper-placement^="right"] {
			.v-popper__arrow-container {
				::after {
					left: 0.125rem;
					transform: translateX(-50%) rotate(-90deg);
				}
			}
		}

		&[data-popper-placement^="top"] {
			.v-popper__wrapper {
				box-shadow: 0rem 0.125rem 0.313rem #6b6b6b;
			}

			.v-popper__arrow-container {
				::after {
					bottom: -0.2rem;
					transform: translateX(-50%) rotate(180deg);
				}
			}
		}
	}
}
</style>
